import React, { useState } from "react";
import { View, StyleSheet, ScrollView } from "react-native";
import NtModalUnactiveButton from "../../components/NtModalUnactiveButton";
import NtModalActiveButton from "../../components/NtModalActiveButton";
import NtModalInputField from "../../components/NtModalInputField";
import NtTabController from "../../components/NtTabController";
import NtToggleRow from "../../components/NtToggleRow";
import extensiontypesdata from "../data/extensiontypesdata";
import orderbyoptionsdata from "../../vvn/data/orderbyoptonsdata";
import NtModalTitle from "../../components/NtModalTitle";
import NtModalSubtitle from "../../components/NtModalSubtitle";
import NtModalInputTitle from "../../components/NtModalInputTitle";

// Mutually-exclusive status filter for analog extensions. The list endpoint
// only knows about `configured=1` and `configured=0`, so we surface it as a
// single-select tab strip (null = "any") rather than two independent toggles.
const analogStatusOptions = [
  { id: "configured", title: "Configured" },
  { id: "unconfigured", title: "Unconfigured" },
];

function NtExtensionAdvancedSearchPicker({
  onCancel,
  onFilter,
  onFilterClear,
  filter,
}) {
  const [extension, setExtension] = useState(filter?.extension || "");
  const [callerIdName, setCallerIdName] = useState(filter?.callerIdName || "");
  const [callerIdNumber, setCallerIdNumber] = useState(
    filter?.callerIdNumber || ""
  );
  const [outboundCallerIdName, setOutboundCallerIdName] = useState(
    filter?.outboundCallerIdName || ""
  );
  const [outboundCallerIdNumber, setOutboundCallerIdNumber] = useState(
    filter?.outboundCallerIdNumber || ""
  );
  const [description, setDescription] = useState(filter?.description || "");
  const [type, setType] = useState(
    extensiontypesdata.find((each) => each.id == filter?.type)
  );
  const initialAnalogStatus = filter?.configured
    ? analogStatusOptions[0]
    : filter?.unConfigured
    ? analogStatusOptions[1]
    : null;
  const [analogStatus, setAnalogStatus] = useState(initialAnalogStatus);
  const [temporary, setTemporary] = useState(!!filter?.temporary);
  const [orderBy, setOrderBy] = useState(
    orderbyoptionsdata.find((each) => each.id == filter?.orderBy)
  );

  const isAnalog = type?.id === "analog";

  const handleClear = () => {
    setExtension("");
    setCallerIdName("");
    setCallerIdNumber("");
    setOutboundCallerIdName("");
    setOutboundCallerIdNumber("");
    setDescription("");
    setType(null);
    setAnalogStatus(null);
    setTemporary(false);
    setOrderBy(null);
    if (onFilterClear) onFilterClear();
  };

  const handleSearch = () => {
    const next = {};
    if (extension) next.extension = extension;
    if (callerIdName) next.callerIdName = callerIdName;
    if (callerIdNumber) next.callerIdNumber = callerIdNumber;
    if (outboundCallerIdName) next.outboundCallerIdName = outboundCallerIdName;
    if (outboundCallerIdNumber)
      next.outboundCallerIdNumber = outboundCallerIdNumber;
    if (description) next.description = description;
    if (type) next.type = type.id;
    if (isAnalog && analogStatus?.id === "configured") next.configured = true;
    if (isAnalog && analogStatus?.id === "unconfigured")
      next.unConfigured = true;
    if (isAnalog && temporary) next.temporary = true;
    if (orderBy) next.orderBy = orderBy.id;
    if (onFilter) onFilter(next);
  };

  return (
    <View style={styles.container}>
      <NtModalTitle>Advanced Search</NtModalTitle>
      <NtModalSubtitle style={[{ marginTop: 4 }]}>
        Select specific parameters for search
      </NtModalSubtitle>

      <ScrollView
        scrollEnabled={true}
        contentContainerStyle={{ flexGrow: 1 }}
        showsVerticalScrollIndicator={false}
      >
        <NtModalInputField
          containerStyle={{ marginTop: 15 }}
          title={"Extension"}
          placeholder={"Extension"}
          value={extension}
          setValue={setExtension}
        />

        <View style={{ flexDirection: "row", marginTop: 15 }}>
          <NtModalInputField
            containerStyle={{ flex: 1 }}
            title={"Caller Name"}
            placeholder={"Caller Name"}
            value={callerIdName}
            setValue={setCallerIdName}
          />
          <NtModalInputField
            containerStyle={{ flex: 1, marginLeft: 10 }}
            title={"Caller Number"}
            placeholder={"Caller Number"}
            value={callerIdNumber}
            setValue={setCallerIdNumber}
          />
        </View>

        <View style={{ flexDirection: "row", marginTop: 15 }}>
          <NtModalInputField
            containerStyle={{ flex: 1 }}
            title={"Outbound Caller Name"}
            placeholder={"Outbound Caller Name"}
            value={outboundCallerIdName}
            setValue={setOutboundCallerIdName}
          />
          <NtModalInputField
            containerStyle={{ flex: 1, marginLeft: 10 }}
            title={"Outbound Caller Number"}
            placeholder={"Outbound Caller Number"}
            value={outboundCallerIdNumber}
            setValue={setOutboundCallerIdNumber}
          />
        </View>

        <NtModalInputField
          containerStyle={{ marginTop: 15 }}
          title={"Description"}
          placeholder={"Description"}
          value={description}
          setValue={setDescription}
        />

        <View style={{ marginTop: 15 }}>
          <NtModalInputTitle style={{ marginLeft: 4 }}>Type</NtModalInputTitle>
          <NtTabController
            containerStyle={{ marginTop: 4 }}
            options={extensiontypesdata}
            selection={type}
            onPress={(item) => {
              if (type?.id === item.id) {
                setType(null);
                setAnalogStatus(null);
                setTemporary(false);
              } else {
                setType(item);
                if (item.id !== "analog") {
                  setAnalogStatus(null);
                  setTemporary(false);
                }
              }
            }}
          />
        </View>

        {isAnalog && (
          <View style={{ marginTop: 15 }}>
            <NtModalInputTitle style={{ marginLeft: 4 }}>
              Status
            </NtModalInputTitle>
            <NtTabController
              containerStyle={{ marginTop: 4 }}
              options={analogStatusOptions}
              selection={analogStatus}
              onPress={(item) => {
                setAnalogStatus(analogStatus?.id === item.id ? null : item);
              }}
            />

            <View style={{ marginTop: 12 }}>
              <NtToggleRow
                value={temporary}
                onChange={setTemporary}
                label="Temporary extensions only"
                sub="Limit results to extensions in the temporary number ranges."
              />
            </View>
          </View>
        )}

        <View style={{ marginTop: 15 }}>
          <NtModalInputTitle style={{ marginLeft: 4 }}>
            Order By
          </NtModalInputTitle>
          <NtTabController
            containerStyle={{ marginTop: 4 }}
            options={orderbyoptionsdata}
            selection={orderBy}
            onPress={(item) => {
              setOrderBy(orderBy?.id === item.id ? null : item);
            }}
          />
        </View>
      </ScrollView>

      <View
        style={{
          flexDirection: "row",
          alignItems: "center",
          justifyContent: "space-between",
          marginTop: 25,
        }}
      >
        <NtModalUnactiveButton title={"Cancel"} onPress={onCancel} />
        <View style={{ flexDirection: "row", alignItems: "center" }}>
          <NtModalUnactiveButton title={"Clear filters"} onPress={handleClear} />
          <NtModalActiveButton
            containerStyle={{ marginLeft: 15 }}
            title={"Search"}
            enabled={true}
            onPress={handleSearch}
          />
        </View>
      </View>
    </View>
  );
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
    minWidth: 500,
    maxWidth: 600,
    padding: 15,
  },
});

export default NtExtensionAdvancedSearchPicker;
